import React, { useState, useMemo } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { STATUS_COLOR_CLASSES } from "../../config/constants";

const ComboBox = ({
  label,
  icon,
  data,
  query,
  setQuery,
  filterData,
  showChevron,
  onSelect,
  labelSize,
  showSelectedResult,
  defaultSelected = null,
  isError,
  placeholder,
  isReadOnly,
}) => {
  const [selectedOption, setSelectedOption] = useState(defaultSelected);
  const comboBoxData = useMemo(
    () =>
      filterData && query
        ? data.filter((result) =>
            result.name.toLowerCase().includes(query.toLowerCase())
          )
        : data,
    [data, filterData, query]
  );
  return (
    <Combobox
      as="div"
      value={selectedOption}
      onChange={(data) => {
        setQuery("");
        onSelect(data);
        if (showSelectedResult) {
          setSelectedOption(data);
        }
      }}
    >
      <Label
        className={`block ${
          labelSize ? labelSize : "text-sm"
        } font-medium text-ink-900`}
      >
        {label}
      </Label>
      <div className="relative mt-2">
        <ComboboxInput
          className={`block w-full truncate rounded-md bg-white py-1.5 pl-2 text-base text-ink-900 outline outline-1 -outline-offset-1 ${
            !isError
              ? "outline-ink-200 focus:outline-accent-500 sm:text-base"
              : "outline-signal-red-500 focus:outline-signal-red-500 sm:text-base"
          }  placeholder:text-ink-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-500 sm:text-base`}
          onChange={(event) => setQuery(event.target.value)}
          onBlur={() => setQuery("")}
          displayValue={(data) => (data ? data.name : "")}
          placeholder={placeholder}
          readOnly={isReadOnly}
        />
        {showChevron && (
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon
              className="size-5 text-ink-400"
              aria-hidden="true"
            />
          </ComboboxButton>
        )}

        {comboBoxData.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            {comboBoxData.map((data) => (
              <ComboboxOption
                key={data.id}
                value={data}
                className="group relative cursor-default select-none py-2 pl-3 pr-9 text-ink-900 data-[focus]:bg-accent-500 data-[focus]:text-white data-[focus]:outline-none"
              >
                <div className="flex items-center w-full">
                  {(data.icon || icon) && (
                    <div className="flex-shrink-0 w-5 h-5">
                      <div className="h-5 w-5 text-ink-400">
                        {data.icon || icon}
                      </div>
                    </div>
                  )}
                  {data?.colour && (
                    <span
                      aria-label={data.name}
                      className={`${
                        STATUS_COLOR_CLASSES[data.colour]
                      } forced-colors:bg-[Highlight] inline-block size-2 shrink-0 rounded-full border border-transparent`}
                    />
                  )}
                  <div className="ml-3 flex-1 min-w-0">
                    <span className="block truncate" title={data.name}>
                      {data.name}
                    </span>
                    {data.secondary ? (
                      <span
                        className="block truncate text-ink-500 group-data-[focus]:text-accent-200"
                        title={data.secondary}
                      >
                        {data.secondary}
                      </span>
                    ) : null}
                  </div>
                </div>

                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-accent-600 group-data-[selected]:flex group-data-[focus]:text-white">
                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                </span>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
};

export default ComboBox;
